import findPrice from 'helpers/src/prices/findPrice';
import CountryService from './CountryService';
import CartService, { IOrder as IBasketItem } from './CartService';
import findFinalPrice from '../helpers/prices/findFinalPrice';
import { ILanguageObject } from 'data-layer/src/interfaces/languageObject';
import { isObservableArray } from 'mobx';
import { canUseWindow } from '../utils';
import { roundPrice } from '../helpers/prices/roundPrice';

interface  IGenerateItemParams {
  getPriceFromCart?: boolean;
  language?: string;
  itemListName?: string;
  getItemListNameFromCart?: boolean;
}

type TItemListName = string | null;

const pageTypes = {
  homepage: 'home', // home page stránka
  category: 'category', // stránka kategorie
  searchResults: 'search_results_page', // stránka s výpisem produktů z vyhledávání
  product: 'product', // stránka produktu
  cart: 'cart', // stránka košíku
  checkout: 'checkout', // objednávkový proces
  purchase: 'purchase', // nákup
  contact: 'contact', // kontaktní stránka
  other: 'other', // ostatní sekce webu (obchodní podmínky atd.)
};

const DEFAULT_ITEM_LIST_NAME: TItemListName = null;

const getPriceWithoutVat = (price: number, vatRate: number) => {
  if (vatRate > 0) {
    return (price * 100 / (100 + vatRate));
  }
  return price;
};

const generateItemListId = (itemListName: string | null): TItemListName => 'string' === typeof itemListName && itemListName.toLowerCase().replace(/\s+/g, '_') || DEFAULT_ITEM_LIST_NAME;

// this is here just to make TypeScript happy
// otherwise if DEFAULT_ITEM_LIST_NAME is string, type errors appear
const typedItemListName = (itemListName: string | null): TItemListName => itemListName;

const generateItem = (
  variant: GQL.IProductVariant,
  quantity: number,
  params?: IGenerateItemParams,
) => {
  const { VATrate } = CountryService.GetInstance();
  const offerPrice = findFinalPrice(variant, false, VATrate);
  const recommendedPrice = findFinalPrice(variant, true, VATrate);
  const prices = {
    price: offerPrice,
    recommendedPrice,
  };  

  if (params && params.getPriceFromCart) {
    prices.recommendedPrice = CartService.GetInstance().getPriceForVariant(variant.id, false);
    prices.price = CartService.GetInstance().getPriceForVariant(variant.id, true);
  }

  const GTMItemListName = params && (params.getPriceFromCart || params.getItemListNameFromCart) && CartService.GetInstance().getGTMItemListName(variant.id) || DEFAULT_ITEM_LIST_NAME;

  if ('number' === typeof VATrate) {
    prices.price = getPriceWithoutVat(prices.price, VATrate);
    prices.recommendedPrice = getPriceWithoutVat(prices.recommendedPrice, VATrate);
  }

  const item = {
    item_id: variant.id,
    item_name: variant.product && (params && params.language && variant.product.name && (variant.product.name[params.language] || variant.product.name.en) || variant.product.title) || null,
    currency: CountryService.GetInstance().currencyCode,
    discount: prices.recommendedPrice > prices.price ? prices.recommendedPrice - prices.price : 0, // (bez DPH)
    price: prices.recommendedPrice > prices.price ? prices.recommendedPrice : prices.price, // (bez DPH)
    index: 0,
    item_brand: variant.product && variant.product.manufacturer && variant.product.manufacturer.name || null,
    item_list_id: params && params.itemListName ? generateItemListId(params.itemListName) : generateItemListId(GTMItemListName),  
    item_list_name: params && params.itemListName || typedItemListName(GTMItemListName),
    item_variant: variant.ean,
    quantity,
  };

  if (variant.product && (Array.isArray(variant.product.categories) || isObservableArray(variant.product.categories))) {
    variant.product.categories.forEach((c, i) => {
      item[`item_category${i > 0 ? i + 1 : ''}`] = c.name && (params && params.language && c.name[params.language] || c.name.en);
    });
  }

  return item;
};

export const generateBasketItems = (variants: GQL.IProductVariant[], cartPrices: boolean = true) => {
  const cartService = CartService.GetInstance();

  let value = 0;
  const items = variants.map((variant, index) => {
    const item = generateItem(variant, cartService.getQuantityForVariant(variant.id), { getPriceFromCart: cartPrices, language: CountryService.GetInstance().language });
    value += (item.price - item.discount) * item.quantity;
    item.index = index;

    return item;
  });
  return { items, value, coupon: cartService.voucherTitle };
};

export default class GoogleTagManagerService {

  // private static lastSearch: string | null;
  private static lastProduct: string | null;
  private static lastProductsInCart: string | null;
  private static lastBeginCheckout: string | null;
  private static lastOrder: string | null;
  // private static lastFBEventId: any | null;
  // private static lastSetCart: any | null;
  private static lastProductsInCategory: any | null;
  private static lastProductsInCategoryList: {[list: string]: string | null} = {};
  private static productsInCategoryListShown: {[pagetype: string]: string[]} = {};
  private static addPaymentShown: boolean;
  private static addShippingShown: boolean;

  private static lastBasketItemQuantities: {
    [id: string]: {
      quantity: number;
      data: any;
    }} = {};

  private static lastURL: string | null;

  /**
   * Do GTM odešle zobrazení produktu
   *
   * @param product
   * @param variant
   */
  public static viewProduct(product: GQL.IProduct, variant: GQL.IProductVariant, itemListName?: string) {
    if (!product || !variant) {
      return;
    }

    // Může se stát, že např. po zobrazení produktu a jeho přidání do košíku, následném zobrazení košíku
    // a kliknutí na produkt v košíku se do GTM pushne virtual_page_view, ale už ne view_item,
    // kdyby to chtěli opravit, tak kromě previous product.id kontrolovat i previous window.location.path
    // a otestovat, jak to funguje

    const currency = CountryService.GetInstance().currency;

    if (GoogleTagManagerService.lastProduct !== product.id) {

      const dataLayer = (window as any).dataLayer;

      if (dataLayer) {
        // GA4
        dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        const item = generateItem(variant, 1, { itemListName });

        dataLayer.push({
          event: 'view_item',
          page_type: 'product', 
          ecommerce: {
            items: [item],
          },
        });
      }

      GoogleTagManagerService.lastProduct = product.id;
    }
  }

  /**
   * Do GTM pošle obsah košíku
   */
  public static productsInCart(items?: IBasketItem[]) {
    if (!items) {
      return;
    }
    const countryService = CountryService.GetInstance();
    
    const itemsJson = JSON.stringify({items, language: countryService.language});

    if (GoogleTagManagerService.lastProductsInCart !== itemsJson) {

      const dataLayer = (window as any).dataLayer;

      if (dataLayer) {
        // GA4
        dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        const { items: productItems, value } = generateBasketItems(items.map(i => i.variant));

        dataLayer.push({
          event: 'view_cart',
          page_type: 'cart',
          currency: countryService.currencyCode,
          value,
          ecommerce: {
            items: productItems,
          },
        });
      }

      GoogleTagManagerService.lastProductsInCart = itemsJson;
    }
  }

  /**
   * Do GTM pošle obsah košíku v kase
   *
   * @param cashBox
   */
  // public static productsInCashBox(cashBox: any[]) {
  //   console.log(cashBox);

  //   if (!cashBox) {
  //     return;
  //   }

  //   const cashBoxJson = JSON.stringify(cashBox);

  //   if (GoogleTagManagerService.lastProductsInCashBox !== cashBoxJson) {

  //     const dataLayer = (window as any).dataLayer;

  //     const productsInCashBox = cashBox.map((item: any) => {
  //       return {
  //         productId: item.variant.id,
  //         productName: item.variant.product.title,
  //         quantity: item.quantity,
  //         size: item.size
  //       };
  //     });

  //     if (dataLayer) {
  //       dataLayer.push({
  //         cashbox: productsInCashBox,
  //       });
  //     }

  //     GoogleTagManagerService.lastProductsInCashBox = cashBoxJson;
  //   }
  // }
  public static beginCheckout(items?: IBasketItem[]) {
    // console.log('productsInCashBox', items);

    if (!items) {
      return;
    }

    GoogleTagManagerService.addPaymentShown = false;
    GoogleTagManagerService.addShippingShown = false;

    const cashBoxJson = JSON.stringify(items);

    // Událost by se měla odesílat kdykoliv, nikoliv pouze při změnách v produktech
    // if (GoogleTagManagerService.lastBeginCheckout !== cashBoxJson) {

      const dataLayer = (window as any).dataLayer;

      if (dataLayer) {

      // GA4
      dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      const { items: cashBoxItems, value } = generateBasketItems(items.map(c => c.variant));

      dataLayer.push({
        event: 'begin_checkout',
        page_type: 'checkout',
        currency: CountryService.GetInstance().currencyCode,
        value,
        ecommerce: {
          items: cashBoxItems,
        },
      });

      GoogleTagManagerService.lastBeginCheckout = cashBoxJson;
    } // }
  }

  /**
   * Do GTM odešle hodnotu právě odeslané objednávky
   *
   * @param order
   */
  public static order(order: GQL.IOrder) {
    if (!order) {
      return;
    }

    const orderJson = JSON.stringify(order);

    if (GoogleTagManagerService.lastOrder !== orderJson) {

      const dataLayer = (window as any).dataLayer;
      if (dataLayer) {
        const vouchers: Array<any> = Array.isArray(order.vouchers) && order.vouchers.filter((v: any) => v.title) || [];
        const voucher = vouchers.length > 0 ? `${vouchers[0].title}${vouchers[0].voucherGroup && vouchers[0].voucherGroup.title ? ` - ${vouchers[0].voucherGroup.title}` : ''}` : null;

        // GA4
        let orderVatRate = 0;
        const items = order.orderedItems.map((orderItem: GQL.IOrderItem, index: number) => {
          const item = generateItem(
            orderItem.variant || {},
            orderItem.quantity,
            {
              language: order.language,
              itemListName: orderItem && orderItem.GTMItemData && 'string' === typeof orderItem.GTMItemData.item_list_name && orderItem.GTMItemData.item_list_name || undefined,
            }
          );
          item.item_name = orderItem.name && (orderItem.name[order.language] || orderItem.name.en) || orderItem.title;
          const VATrate = 21;
          item.price = getPriceWithoutVat(orderItem.price, VATrate); // (bez DPH)
          (item as any).index = index;
          if (voucher) {
            (item as any).coupon = voucher;
          }
          if (VATrate && VATrate > orderVatRate) {
            orderVatRate = VATrate;
          }
          return item;
        });
        const value = getPriceWithoutVat(order.total, orderVatRate);
        const tax = order.total - value;

        dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        dataLayer.push({
          event: 'purchase',
          page_type: 'purchase',
          ecommerce: {
            transaction_id: order.orderNumber,
            value: roundPrice(value), // (bez DPH)
            tax: roundPrice(tax),
            shipping: roundPrice(getPriceWithoutVat((order.deliveryTotal || 0) + order.paymentTotal, 21)),
            currency: CountryService.GetInstance().currencyCode,
            coupon: voucher,
            items,
          },
        });
      }

      GoogleTagManagerService.lastOrder = orderJson;
    }
  }

  public static addToOrRemoveFromBasket (items: IBasketItem[]) {
    for (const item of items) {
      if (!item.variant || !item.variant.id) {
        continue;
      }
      let ecommerceItem = null;
      if (!GoogleTagManagerService.lastBasketItemQuantities[item.variant.id]) {
        ecommerceItem = GoogleTagManagerService.addToBasket(item.variant, item.quantity);
      } else {
        if (item.quantity > GoogleTagManagerService.lastBasketItemQuantities[item.variant.id].quantity) {
          ecommerceItem = GoogleTagManagerService.addToBasket(item.variant, item.quantity - GoogleTagManagerService.lastBasketItemQuantities[item.variant.id].quantity);
        } else if (item.quantity < GoogleTagManagerService.lastBasketItemQuantities[item.variant.id].quantity) {
          ecommerceItem = GoogleTagManagerService.removeFromBasket(
            GoogleTagManagerService.lastBasketItemQuantities[item.variant.id].data,
            GoogleTagManagerService.lastBasketItemQuantities[item.variant.id].quantity - item.quantity
          );
        }
      }
      if (ecommerceItem) {
        GoogleTagManagerService.lastBasketItemQuantities[item.variant.id] = {
          quantity: item.quantity,
          data: ecommerceItem,
        };
      }
    }

    for (const itemId of Object.keys(GoogleTagManagerService.lastBasketItemQuantities)) {
      if (!items.find(i => i.variant.id === itemId)) {
        GoogleTagManagerService.removeFromBasket(
          GoogleTagManagerService.lastBasketItemQuantities[itemId].data,
          GoogleTagManagerService.lastBasketItemQuantities[itemId].quantity
        );
        delete GoogleTagManagerService.lastBasketItemQuantities[itemId];
      }
    }
  }

  private static addToBasket (variant: GQL.IProductVariant, quantity: number): any {
    const dataLayer = (window as any).dataLayer;

    const item = generateItem(variant, quantity, { getItemListNameFromCart: true });

    if (dataLayer) {
      // GA4
      dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          items: [item],
        },
      });
    }
    return item;
  }

  private static removeFromBasket (ecommerceInputItem: {[prop: string]: string | number, quantity: number}, removeQuantity: number) {
    const dataLayer = (window as any).dataLayer;

    const ecommerceItem = {
      ...ecommerceInputItem,
      quantity: removeQuantity,
    };

    if (dataLayer) {
      // GA4
      dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          items: [ecommerceItem],
        },
      });
    }
    return ecommerceItem;
  }

  public static addShipping (shipping: GQL.IDeliveryMethod, language: string) {
    if (GoogleTagManagerService.addShippingShown) {
      return;
    }
    const dataLayer = (window as any).dataLayer;
    if (!GoogleTagManagerService.lastBeginCheckout) {
      return;
    }

    let variants = null;
    try {
      variants = JSON.parse(GoogleTagManagerService.lastBeginCheckout).map((c: any) => c.variant);
    } catch (e) {
      return;
    }

    if (dataLayer) {
      // GA4
      dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      const { items, value, coupon } = generateBasketItems(variants);

      dataLayer.push({
        event: 'add_shipping_info',
        page_type: 'checkout',
        currency: CountryService.GetInstance().currencyCode,
        value,
        ecommerce: {
          // Na Divesoft nejsou coupon a shipping_tier v zadání
          // coupon,
          // shipping_tier: shipping && shipping.name && (shipping.name[language] || shipping.name.en) || null,
          items,
        },
      });

      GoogleTagManagerService.addShippingShown = true;
    }
  }

  public static addPayment (payment: { name: ILanguageObject }) {
    if (GoogleTagManagerService.addPaymentShown) {
      return;
    }
    const dataLayer = (window as any).dataLayer;
    if (!GoogleTagManagerService.lastBeginCheckout) {
      return;
    }

    let variants = null;
    try {
      variants = JSON.parse(GoogleTagManagerService.lastBeginCheckout).map((c: any) => c.variant);
    } catch (e) {
      return;
    }

    if (dataLayer) {
      // GA4
      dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      const { items, value, coupon } = generateBasketItems(variants);

      dataLayer.push({
        event: 'add_payment_info',
        page_type: 'checkout',
        currency: CountryService.GetInstance().currencyCode,
        value,
        ecommerce: {
          // Na Divesoft nejsou coupon a payment_type v zadání
          // coupon,
          // payment_type: payment && payment.name && payment.name[CountryService.GetInstance().language] || undefined,
          items,
        },
      });

      GoogleTagManagerService.addPaymentShown = true;
    }
  }

  public static clickProduct (variant: GQL.IProductVariant, itemListName?: string) {
    const dataLayer = (window as any).dataLayer;

    if (dataLayer) {
      // GA4
      dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      const item = generateItem(variant, 1, { itemListName });

      dataLayer.push({
        event: 'select_item',
        page_type: 'category', 
        item_list_id: itemListName ? generateItemListId(itemListName) : generateItemListId(DEFAULT_ITEM_LIST_NAME),  
        item_list_name: itemListName || DEFAULT_ITEM_LIST_NAME,
        ecommerce: {
          items: [{
            ...item,
            index: 1,
          }],
        },
      });
    }
  }

  public static productsInCategory (variants: GQL.IProductVariant[], params: { searchQuery?: string, pageType?: string, list?: string, itemListName?: string }) {
    if (variants.length < 1 && !params.searchQuery) {
      return;
    }

    const productsInCategoryJSON = JSON.stringify({variants, params});

    const lastProducts = params.list ? GoogleTagManagerService.lastProductsInCategoryList[params.list] : GoogleTagManagerService.lastProductsInCategory;

    if (lastProducts === productsInCategoryJSON) {
      return;
    }

    const dataLayer = (window as any).dataLayer;

    if (dataLayer) {
      // GA4
      dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      const { items } = generateBasketItems(variants, false);

      const event = {
        event: 'view_item_list',
        page_type: 'category',
        item_list_id: generateItemListId(DEFAULT_ITEM_LIST_NAME),
        item_list_name: DEFAULT_ITEM_LIST_NAME,
        ecommerce: {
          items: items.map((i) => ({
            ...i,
            quantity: 1,
          })),
        },
      };

      if (params.pageType) {
        event.page_type = params.pageType;
      }

      if ('string' === typeof params.itemListName) {
        event.item_list_name = params.itemListName;
        event.item_list_id = generateItemListId(params.itemListName);
        event.ecommerce.items = event.ecommerce.items.map((i) => ({
          ...i,
          item_list_id: event.item_list_id,
          item_list_name: event.item_list_name,
        }));
      }

      if (params.searchQuery) {
        event.event = 'view_search_results';
        event.page_type = 'seach_results_page';
        event.item_list_id = 'search_results';
        event.item_list_name = 'Search results';
        (event as any).search_term = params.searchQuery;
        event.ecommerce.items = event.ecommerce.items.map((i) => ({
          ...i,
          item_list_id: event.item_list_id,
          item_list_name: event.item_list_name,
        }));
      }

      // Na homepage docházelo k tomu, že view_item_list se spouštěl vícenásobně pro jednotlivé sekce
      // (prověřit jestli to bylo kvůli tomu, že produkty jsou řazeny náhodně a došlo k překreslení jinými produkty)
      // takže se kontroluje, jestli už pro danou sekci byl view_item_list spuštěn
      // seznam spuštěných view_item_list eventů se musí mazat v při spuštění virtual_page_view
      if (
        ('string' === typeof params.itemListName)
        && Array.isArray(GoogleTagManagerService.productsInCategoryListShown[event.page_type])
        && GoogleTagManagerService.productsInCategoryListShown[event.page_type].indexOf(params.itemListName) > -1) {
          // don't push to dataLayer, as the list is already shown
          return;
      }

      if (!Array.isArray(GoogleTagManagerService.productsInCategoryListShown[event.page_type])) {
        GoogleTagManagerService.productsInCategoryListShown[event.page_type] = [];
      }
      if (('string' === typeof params.itemListName) && GoogleTagManagerService.productsInCategoryListShown[event.page_type].indexOf(params.itemListName) < 0) {
        GoogleTagManagerService.productsInCategoryListShown[event.page_type].push(params.itemListName);
      }

      dataLayer.push(event);
    }

    if (params.list) {
      GoogleTagManagerService.lastProductsInCategoryList[params.list] = productsInCategoryJSON;
    } else {
      GoogleTagManagerService.lastProductsInCategory = productsInCategoryJSON;
    }
  }

  public static virtualPageView(type?: keyof typeof pageTypes) {
    let pageType = type ? pageTypes[type] : undefined;

    // nepoužívat window.location.href, protože ten obsahuje i protokol a server
    const url = canUseWindow() && window.location && `${window.location.pathname}${window.location.search}` || null;
    if (url === GoogleTagManagerService.lastURL) {
      return;
    }
    if (
      GoogleTagManagerService.lastURL
      && (GoogleTagManagerService.lastURL !== '/')
      && url
      && url.includes(GoogleTagManagerService.lastURL)
      && !url.includes('page=')
    ) {
      return;
    }

    if (!pageType && canUseWindow() && window.location && window.location.pathname) {
      if ('/' === window.location.pathname) {
        pageType = pageTypes.homepage;
      } else if (window.location.search && window.location.search.includes('q=')) {
        pageType = pageTypes.searchResults;
      } else if ('/cart' === window.location.pathname) {
        pageType = pageTypes.cart;
      } else if (['/personal-data', '/payment-and-shipping'].indexOf(window.location.pathname) > -1 ) {
        pageType = pageTypes.checkout;
      } else if (window.location.pathname.includes('/order-finished')) {
        pageType = pageTypes.purchase;
      } else if (window.location.pathname.includes('/products/')) {
        pageType = pageTypes.category;
      } else if (window.location.pathname.includes('/user/')) {
        pageType = pageTypes.other;
      }
    }

    if (pageType && GoogleTagManagerService.productsInCategoryListShown[pageType]) {
      // Mazání evicence spuštěných view_item_list eventů, aby se při dalším načtení stejné stránky
      // eventy view_item_list opět spustily
      delete GoogleTagManagerService.productsInCategoryListShown[pageType];
    }

    if (pageType) {
      const dataLayer = (window as any).dataLayer;

      if (dataLayer) {
        dataLayer.push({
          event : 'virtual_page_view',
          page_type: pageType,
        });
      }

      GoogleTagManagerService.lastURL = url;
    }
  }

  public static startSearch() {
    const dataLayer = (window as any).dataLayer;

    if (dataLayer) {
      dataLayer.push({
        event : 'search_start'
      });
    }
  }

  // Registrace k odběru newsletteru se neprovádí na eshopu a po úspěšné registraci není návštěvník vrácen na eshop
  // není možné spouštět na eshopu
  public static ʬ_newsletterSignUp() {
    const dataLayer = (window as any).dataLayer;

    if (dataLayer) {
      dataLayer.push({
        event: 'newsletter_signup',
      });
    }
  }

  // Kontaktní formulář není na eshopu, jenom na hlavních stránkách
  public static ʬ_contactFormSent() {
    const dataLayer = (window as any).dataLayer;

    if (dataLayer) {
      dataLayer.push({
        event: 'contact_form_sent',
      });
    }
  }

  public static startRegistration() {
    const dataLayer = (window as any).dataLayer;

    if (dataLayer) {
      dataLayer.push({
        event: 'registration_started',
      });
    }
  }

  public static registrationCompleted() {
    const dataLayer = (window as any).dataLayer;

    if (dataLayer) {
      dataLayer.push({
        event: 'registration_completed',
      });
    }
  }

  public static startSignIn() {
    const dataLayer = (window as any).dataLayer;

    if (dataLayer) {
      dataLayer.push({
        event: 'sign_in_started',
      });
    }
  }

  public static signedIn() {
    const dataLayer = (window as any).dataLayer;

    if (dataLayer) {
      dataLayer.push({
        event: 'sign_in_completed',
      });
    }
  }
}


// WEBPACK FOOTER //
// ./src/services/GoogleTagManagerService.ts